import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdOutlineCancel } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Textarea,
  Image,
  Box,
  Flex,
  Stack,
  Text,
  VStack,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  addSymptom,
  removeSymptom,
  setCurrentStep,
  setStep2,
  useAppDispatch,
  useAppSelector,
} from '@spartalabs/pdc-core';
import { Card, Container, Unauthorized } from 'components';
import * as z from 'zod';
import trashSvg from '../../../../assets/svg/trash.svg';
import { NewScheduleHeader } from '../NewScheduleHeader';
import Service from './components/Service';

const ServicesFormData = z.object({
  observation: z.string().nonempty('Campo obrigatório'),
});

type Step2FormSchema = z.infer<typeof ServicesFormData>;

export const ServicesForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { step2 } = useAppSelector(state => state.scheduleSlicer);
  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const services = useMemo(() => {
    return step2.symptoms;
  }, [step2.symptoms]);

  const [addingService, setAddingService] = useState<
    ScheduleSymptom | undefined
  >();
  const onAddingServiceChange = useCallback(
    (changedAddingService: ScheduleSymptom) => {
      setAddingService(changedAddingService);
    },
    [],
  );
  const [images, setImages] = useState(step2?.files || []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Step2FormSchema>({
    resolver: zodResolver(ServicesFormData),
  });

  const handleAddService = (service: ScheduleSymptom) => {
    const newService = service;
    newService.details = service.details?.filter(
      detail => detail !== undefined,
    );
    dispatch(addSymptom(newService));
  };

  const handleNotAddedService = (service: ScheduleSymptom) => {
    const newService = service;
    newService.details = service.details?.filter(
      detail => detail !== undefined,
    );

    return newService;
  };

  const handleRemoveService = (service: ScheduleSymptom) => {
    dispatch(removeSymptom(service));
  };

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => {
    const selectedFiles = event.target.files;

    if (selectedFiles) {
      const newPreviewImages = Array.from(selectedFiles).map(file => {
        const reader = new FileReader();

        return new Promise<string>((resolve, reject) => {
          reader.onload = function (event) {
            if (event.target?.result) {
              resolve(event.target.result.toString());
            } else {
              reject(new Error('Failed to read the file.'));
            }
          };

          reader.onerror = function (event) {
            reject(
              event.target?.error ?? new Error('Failed to read the file.'),
            );
          };

          reader.readAsDataURL(file as Blob);
        });
      });

      Promise.all(newPreviewImages)
        .then(result => {
          const names = Array.from(selectedFiles).map(
            (file: File) => file.name,
          );
          setImages(prevState => [
            ...prevState,
            { file: result[0], name: names[0], id },
          ]);
        })
        .catch();
    }
  };

  const handleDeleteImage = (name: string) => {
    setImages(images => images.filter(image => image.name !== name));
  };

  const onSubmit = (data: Step2FormSchema) => {
    if (
      addingService &&
      ((!!addingService.observation && addingService.observation !== '') ||
        addingService.details?.[0])
    ) {
      const notAddedService = handleNotAddedService(addingService);

      dispatch(
        setStep2({
          symptoms: [...services, notAddedService],
          observation: data.observation,
          files: images,
        }),
      );
    } else {
      dispatch(
        setStep2({
          symptoms: services,
          observation: data.observation,
          files: images,
        }),
      );
    }

    navigate('/novo-agendamento/agenda');
    dispatch(setCurrentStep(3));
  };

  const handlePreviousStep = () => {
    navigate('/novo-agendamento/veiculos');
    dispatch(setCurrentStep(1));
    dispatch(
      setStep2({
        symptoms: services,
        observation: getValues('observation'),
        files: images,
      }),
    );
  };

  if (!permissions?.includes('createSchedule')) return <Unauthorized />;
  return (
    <Container mx="0px" mt="0px" justifyContent="center">
      <NewScheduleHeader step={2} />
      <Flex
        pt="32px"
        flexDir="column"
        align="center"
        justify="center"
        mb="32px"
      >
        <Text fontSize="18px" color="text.100">
          Serviços Necessários
        </Text>
        <Text fontSize="14px" color="text.300" mt="3px">
          Selecione os serviços necessários
        </Text>
      </Flex>

      <VStack
        w="100%"
        maxW="400px"
        alignItems="center"
        spacing="24px"
        justifyContent="center"
        mb="26px"
        mx="auto"
      >
        <Card w="100%" p={0} minH={0}>
          <Service
            images={images}
            handleAddImage={handleImageChange}
            ref={onAddingServiceChange}
            handleAddService={service => handleAddService(service)}
            handleDeleteImage={handleDeleteImage}
          />
        </Card>

        <Card w="100%" p={0} minH={0}>
          {services.map((service, index: number) => (
            <HStack key={index} w="100%" position="relative">
              <Service
                handleDeleteImage={handleDeleteImage}
                images={images}
                handleAddImage={handleImageChange}
                service={service}
                hasDivider={index < services.length - 1}
                handleAddService={service => handleAddService(service)}
              />
              <IconButton
                aria-label="Apagar serviço"
                position="absolute"
                right="-35px"
                variant="ghost"
                onClick={() => handleRemoveService(service)}
                icon={<MdOutlineCancel />}
                h="20px"
                w="20px"
                color="text.100"
                alignSelf="start"
                top="14px"
              />
            </HStack>
          ))}
        </Card>

        <Card mb="36px" w="100%">
          <Stack mb="16px">
            <Text
              aria-hidden="true"
              as="label"
              fontSize="14px"
              color="text.300"
            >
              *Observações Gerais
            </Text>
            <>
              <Textarea
                aria-label="Adicione aqui Observações Gerais"
                variant="unstyled"
                bg="transparent"
                border="1px solid"
                borderColor="grey.600"
                color="text.100"
                fontSize="16px"
                fontWeight="500"
                p="3"
                size="lg"
                defaultValue={step2?.observation || ''}
                placeholder="Digite sua observação"
                {...register('observation')}
              />
              {errors?.observation && (
                <Text alignSelf="start" pl="8px" color="red.200" fontSize="sm">
                  {errors.observation.message}
                </Text>
              )}
            </>
          </Stack>

          <Text as="label" fontSize="14px" color="text.300">
            Deseja anexar imagens a sua solicitação?
          </Text>

          <Flex
            mt="8px"
            w="80%"
            align="center"
            justify="space-between"
            borderColor="text.100"
            borderStyle="dashed"
            borderWidth="1px"
            rounded="md"
            shadow="sm"
            role="group"
            py="15px"
            px="24px"
            h="54px"
            transition="all 150ms ease-in-out"
            _hover={{
              shadow: 'md',
            }}
            position="relative"
          >
            <Text fontSize="12px" color="text.100">
              Insira um arquivo se necessário
            </Text>
            <Input
              cursor="pointer"
              type="file"
              h="100%"
              width="100%"
              position="absolute"
              multiple
              top="0"
              left="0"
              bottom="0"
              opacity="0"
              aria-hidden="true"
              accept="image/png, image/jpg, image/jpeg"
              border="3px solid red"
              onChange={e => handleImageChange(e, '1')}
            />
            <Flex bg="grey.200" py="8px" px="10px" rounded="md">
              <Text fontSize="14px" fontWeight="normal" color="black">
                Upload
              </Text>
            </Flex>
          </Flex>

          <Flex mt="24px" align="start" flexDir="column" mb="36px">
            {images
              .filter(image => image.id === '1')
              .map((preview: ScheduleFile, index: number) => (
                <Flex w="100%" align="center" key={index}>
                  <Box>
                    <Image
                      width="32px"
                      height="32px"
                      rounded="md"
                      src={preview.file}
                    />
                  </Box>
                  <Text
                    noOfLines={1}
                    ml="20px"
                    color="black"
                    wordBreak="break-all"
                  >
                    {preview.name}
                  </Text>
                  <Button
                    bg="transparent"
                    p="0"
                    _hover={{ bg: 'transparent' }}
                    onClick={() => handleDeleteImage(preview.name)}
                  >
                    <Image src={trashSvg} />
                  </Button>
                </Flex>
              ))}
          </Flex>

          <HStack w="100%" justifyContent="flex-end">
            <Button variant="outline" onClick={handlePreviousStep}>
              Voltar
            </Button>

            <Button rounded="4px" onClick={handleSubmit(onSubmit)}>
              Continuar
            </Button>
          </HStack>
        </Card>
      </VStack>
    </Container>
  );
};
